import { useEffect } from 'react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { watchShellSettings } from '@/features/desktop/shellSettings';
import { setDesktopAttribution } from './desktopNotifications';
import { useBadgeSync, useNotificationFeed } from './hooks/useNotifications';
import { useSettingsHydration } from './hooks/useNotificationSettings';

/**
 * Feeds the shell the attribution line for its native toasts: which workspace,
 * which account. It is the one piece of context a notification cannot get from
 * the message itself — and the reason the in-app card carried it too.
 *
 * Pushed into module state rather than read through a hook because
 * `showDesktopNotification` runs on a socket callback, outside React, where a
 * burst of traffic must cost no renders.
 */
function useDesktopAttribution(): void {
  const { user, tenant } = useAuth();
  const workspace = tenant?.name ?? '';
  const account = user?.email ?? '';

  useEffect(() => {
    setDesktopAttribution({ workspace, account });
  }, [workspace, account]);
}

/**
 * Headless. Mount once inside the app providers: hydrates settings, opens the
 * gateway subscription and keeps the OS badge in sync. Renders nothing, so
 * inbound traffic never re-renders the shell.
 */
export function NotificationRuntime() {
  useSettingsHydration();
  useNotificationFeed();
  useBadgeSync();
  useDesktopAttribution();
  /* Learn whether the shell draws banners BEFORE the first one is due. */
  useEffect(() => {
    watchShellSettings();
  }, []);
  return null;
}
